import React, { useEffect, useRef, useCallback, useMemo } from "react";
import ReactTooltip from "react-tooltip";

import PropTypes from "prop-types";
import { Grid } from "semantic-ui-react";

import VerticalSpacing from "@cayuse/shared-react.components.vertical-spacing";
import { isNonEmptyArray, isNonEmptyString } from "@cayuse/shared-react.utils.type-checks";

import { COLUMN_HEADER_KEYS } from "globals/constants";
import { useDictionary, useMediaQuery } from "hooks";
import { getColumnHeaderByShortLabel } from "utils";


import FormSummaryTable from "./FormSummaryTable";
import styles from "./styles.css";



const FormSummary = ({
	summaryTitle,
	actionButtons,
	statusDropdown,
	formInstance,
	defaultColumnHeaders,
	formType,
	thirdColumnContent,
}) => {
	const dictionary = useDictionary("proposals.summary"); //TODO: modify dictionary?
	const valueListRef = useRef(null);
	const observerRef = useRef(null);

	const isTabletView = useMediaQuery("(max-width: 900px)");

	const activeColumnHeaders = useMemo(() => {
		const active = [];
		const availableColumnHeaders = isNonEmptyArray(formInstance.instance?.formElements)
			? formInstance.instance.formElements
				.filter(({ columnHeader }) => columnHeader !== "null" && isNonEmptyString(columnHeader))
				.map(({ id, columnHeader, type }) => ({
					keys: [ id ],
					columnHeader,
					type,
				}))
			: [];

		for (const defaultHeader of defaultColumnHeaders) {
			const matchedColumnHeader = !defaultHeader.isPlaceholder
				? getColumnHeaderByShortLabel(availableColumnHeaders, defaultHeader.columnHeader)
				: false;

			active.push(matchedColumnHeader || { ...defaultHeader });
		}

		return active;
	}, [ defaultColumnHeaders, formInstance ]);

	const attachToolTips = useCallback(listElement => {
		listElement.querySelectorAll(`div.${styles.summaryValueLayout} > *`)
			.forEach(element => {
				const isOverflowing = element.scrollWidth > element.clientWidth;
				const hasText = isNonEmptyString(element.textContent);

				if (isOverflowing && hasText) {
					element.dataset.tip = element.textContent;
					element.dataset.for = "react-tooltip";
				} else {
					delete element.dataset.tip;
					delete element.dataset.for;
				}
			});

		ReactTooltip.rebuild();
	}, []);

	useEffect(() => { /* watch for changes to the summary elements, set tooltips for overflowing values */
		if (valueListRef.current) {
			const summaryListElement = valueListRef.current;

			if (!observerRef.current) {
				observerRef.current = new MutationObserver(() => attachToolTips(summaryListElement));
			}

			observerRef.current.observe(summaryListElement, { childList: true, subtree: true });

			return () => {
				observerRef.current.disconnect();
			};
		}
	}, [ attachToolTips ]);

	const hasThirdColumn = thirdColumnContent && formType !== COLUMN_HEADER_KEYS.SUBAWARD;
	const hasFirstColumn = thirdColumnContent && formType === COLUMN_HEADER_KEYS.SUBAWARD;
	const numberOfChildGridColumns = hasThirdColumn || hasFirstColumn  ? 2 : 1;
	const childGridFirstColumnWidth = hasThirdColumn || hasFirstColumn ? 12 : 16;
	const numColumns = formType === COLUMN_HEADER_KEYS.SUBAWARD ? 2 : 3;

	const FormSummaryDesktopView = () => {
		return (
			<Grid className={styles.parentGrid} celled columns={2}>
				<Grid.Row>
					{actionButtons && <Grid.Column className={styles.parentGridColumn} width={3}>
						<h2>{dictionary.myActions || "My Actions"}</h2>
						<div className="actionButtons">
							{actionButtons}
						</div>
					</Grid.Column>}
					<Grid.Column className={styles.parentGridColumn} width={actionButtons ? 13 : 16}>
						<h2>{summaryTitle}</h2>
						{statusDropdown}
						<Grid celled="internally" columns={numberOfChildGridColumns}>
							<Grid.Row>
								{hasFirstColumn && <Grid.Column className={styles.childGridColumn} width={4}>
									{thirdColumnContent}
								</Grid.Column>}
								<Grid.Column className={styles.childGridColumn} width={childGridFirstColumnWidth}>
									<div ref={valueListRef}>
										<FormSummaryTable
											activeColumnHeaders={activeColumnHeaders}
											formInstanceValues={formInstance.values}
											numColumns={numColumns}
											isTabletView={isTabletView}
										/>
									</div>
								</Grid.Column>
								{hasThirdColumn && (
									<Grid.Column className={styles.childGridColumn} width={4}>
										{thirdColumnContent}
									</Grid.Column>
								)}
							</Grid.Row>
						</Grid>
					</Grid.Column>
				</Grid.Row>
			</Grid>
		);
	};

	const FormSummaryTabletView = () => {
		return (
			<div className={styles.formSummaryTabletWrapper}>
				<div className={styles.formSummaryTitleWrapper}>
					<h3>{summaryTitle}</h3>
					{statusDropdown}
				</div>
				{actionButtons && (
					<div className={styles.formSummaryActionsWrapper}>
						<h3>{dictionary.myActions || "My Actions"}</h3>
						<div className={styles.formSummaryActionButtons}>
							{actionButtons}
						</div>
					</div>
				)}
				{hasFirstColumn && (
					<div className={styles.tabletViewThirdColumnContent}>
						{thirdColumnContent}
					</div>
				)}
				<div ref={valueListRef}>
					<FormSummaryTable
						activeColumnHeaders={activeColumnHeaders}
						formInstanceValues={formInstance.values}
						numColumns={numColumns}
						isTabletView={isTabletView}
					/>
				</div>
				{hasThirdColumn && (
					<div className={styles.tabletViewThirdColumnContent}>
						{thirdColumnContent}
					</div>
				)}
			</div>
		);
	};

	return (
		<div id="formSummary">
			<VerticalSpacing>
				{isTabletView ? (
					<FormSummaryTabletView />
				) : (
					<FormSummaryDesktopView />
				)}
			</VerticalSpacing>
		</div>
	);
};

FormSummary.propTypes = {
	summaryTitle: PropTypes.string,
	actionButtons: PropTypes.arrayOf(PropTypes.node),
	statusDropdown: PropTypes.node,
	formInstance: PropTypes.shape({
		instance: PropTypes.object,
		instanceId: PropTypes.string.isRequired,
		values: PropTypes.object
	}),
	defaultColumnHeaders: PropTypes.arrayOf(PropTypes.object),
	thirdColumnContent: PropTypes.oneOfType([
		PropTypes.node, PropTypes.string, PropTypes.number
	])
};

export default FormSummary;
